import { useCallback, useMemo } from 'react';

import { useServiceRequestsPaginated } from '@commenda/api/service-request';

import { TaskParty } from '@commenda/prisma';
import { IndirectTaxClientTaskHelper } from '../helpers';
import { IndirectTaxClientTask, IndirectTaxRegion } from '../types';
import { useIndirectTaxFilingAccess } from './useIndirectTaxFilingAccess';

interface UseIndirectTaxClientTasksParams {
  corporationId: number;
}

interface UseIndirectTaxClientTasksResult {
  tasks: IndirectTaxClientTask[];
  pendingCount: number;
  isLoading: boolean;
  error: unknown;
  mutate: () => void;
}

// Exposure service requests are intentionally NOT surfaced in this
// hook. They appear on the homepage GlobalInbox (which fetches its own rooms)
// and clicking one deep-links to the exposure dashboard, where the per-region
// "Register" button on the exposure card is the action. Showing them here too
// would duplicate the call-to-action and ship users to the wrong page when
// they click the in-task-bar Register button.
export function useIndirectTaxClientTasks({
  corporationId,
}: UseIndirectTaxClientTasksParams): UseIndirectTaxClientTasksResult {
  const { isInternalUser } = useIndirectTaxFilingAccess(
    IndirectTaxRegion.ALL_REGIONS
  );
  const {
    data: registrationServiceRequests,
    isLoading: isLoadingRegistrations,
    error: registrationsError,
    mutate: mutateRegistrations,
  } = useServiceRequestsPaginated({
    corporationId,
    includeServiceTypes: ['SALES_TAX_REGISTRATION'],
    includedStatuses:
      IndirectTaxClientTaskHelper.getActiveRegistrationServiceRequestStatuses(),
  });

  const {
    data: filingServiceRequests,
    isLoading: isLoadingFilings,
    error: filingsError,
    mutate: mutateFilings,
  } = useServiceRequestsPaginated({
    corporationId,
    includeServiceTypes: ['SALES_TAX_FILING'],
    includedStatuses:
      IndirectTaxClientTaskHelper.getActiveFilingServiceRequestStatuses(),
  });

  const tasks = useMemo<IndirectTaxClientTask[]>(() => {
    const registrationTasks =
      IndirectTaxClientTaskHelper.deriveRegistrationClientTasks(
        registrationServiceRequests ?? []
      );
    const filingTasks = IndirectTaxClientTaskHelper.deriveFilingClientTasks(
      filingServiceRequests ?? [],
      TaskParty.CLIENT,
      isInternalUser
    );
    return [...registrationTasks, ...filingTasks];
  }, [registrationServiceRequests, filingServiceRequests, isInternalUser]);

  const mutate = useCallback(() => {
    mutateRegistrations();
    mutateFilings();
  }, [mutateRegistrations, mutateFilings]);

  return {
    tasks,
    pendingCount: tasks.length,
    isLoading: isLoadingRegistrations || isLoadingFilings,
    error: registrationsError ?? filingsError,
    mutate,
  };
}
